import { type FC, useContext } from 'react';
import classnames from 'classnames';
import { graphql, useFragment } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import QuestionMarkCircle from 'dibs-icons/exports/QuestionMarkCircle.ts';
import Receipt from 'dibs-icons/exports/Receipt.ts';
import { GetHelpZendeskArticlesSearch } from './GetHelpZendeskArticlesSearch.tsx';
import { GetHelpZendeskIntroLink } from './GetHelpZendeskIntroLink.tsx';
import { GetHelpZendeskBrowseByTopic } from './GetHelpZendeskBrowseByTopic.tsx';

import { trackGetHelpZendeskEvent } from './getHelpZendeskTracking.ts';
import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { STEPS, ACTIONS } from './GetHelpZendeskConstants.ts';

import { type GetHelpZendeskModalIntro_viewer$key } from './__generated__/GetHelpZendeskModalIntro_viewer.graphql.ts';

import dibsCss from 'dibs-css';

const GetHelpZendeskModalIntro: FC<{ viewer: GetHelpZendeskModalIntro_viewer$key }> = ({
    viewer: viewerRef,
}) => {
    const {
        dispatch,
        state: { isSeller, isCheckout, renderAs },
    } = useContext(GetHelpZendeskContext);

    const viewer = useFragment(
        graphql`
            fragment GetHelpZendeskModalIntro_viewer on Viewer {
                user(userId: $userId) @include(if: $hasUserId) {
                    profile {
                        firstName
                    }
                }
                getHelpCmsTopics {
                    groupKey
                    topics {
                        topicKey
                        topicLabel
                    }
                }
                ...GetHelpZendeskArticlesSearch_viewer
                ...GetHelpZendeskBrowseByTopic_viewer
            }
        `,
        viewerRef
    );

    const { user, getHelpCmsTopics } = viewer;
    const firstName = user?.profile?.firstName || '';
    const checkoutCmsTopics =
        getHelpCmsTopics?.find(topic => topic?.groupKey === 'checkoutAssistance')?.topics || [];

    const isLoggedOut = renderAs === 'loggedOut';

    return (
        <div className={dibsCss.mbLarge}>
            <div
                className={classnames(
                    dibsCss.sassyFontHeaderMedium,
                    dibsCss.mbMedium,
                    dibsCss.pb0,
                    dibsCss.textNoir,
                    dibsCss.textCenter
                )}
            >
                {firstName ? (
                    <FormattedMessage
                        id="getHelp.GetHelpZendeskModalIntro.introHeaderLoggedIn"
                        defaultMessage="Hi {firstName}, what can we help you with?"
                        values={{
                            firstName: (
                                <span className={dibsCss.sassyFontHeaderMediumItalic}>
                                    {firstName}
                                </span>
                            ),
                        }}
                    />
                ) : (
                    <FormattedMessage
                        id="getHelp.GetHelpZendeskModalIntro.header"
                        defaultMessage="What can we help you with?"
                    />
                )}
            </div>
            <div>
                <div className={dibsCss.mbMedium}>
                    <GetHelpZendeskArticlesSearch viewer={viewer} />
                </div>
                <div
                    className={classnames(
                        dibsCss.sassyFontBodyMediumHeavy,
                        dibsCss.textIron,
                        dibsCss.ptXsmall
                    )}
                >
                    <FormattedMessage
                        id="getHelp.GetHelpZendeskModalIntro.quickAnswers"
                        defaultMessage="Quick Answers"
                    />
                </div>
                {isCheckout && !!checkoutCmsTopics.length && (
                    <div className={dibsCss.mtSmall}>
                        {checkoutCmsTopics.map(topic => (
                            <div key={topic?.topicKey} className={dibsCss.mbXxsmall}>
                                <Link
                                    onClick={() => {
                                        dispatch({
                                            type: ACTIONS.SET_CMS_TOPIC_KEY,
                                            topicKey: topic?.topicKey || '',
                                        });
                                        dispatch({ type: ACTIONS.SET_STEP, step: STEPS.CMS_TOPIC });
                                    }}
                                    underline="none"
                                >
                                    <span className={dibsCss.sassyFontBodyMediumHeavy}>
                                        {topic?.topicLabel}
                                    </span>
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
                {isSeller && (
                    <>
                        <GetHelpZendeskIntroLink
                            onClick={() => {
                                trackGetHelpZendeskEvent({
                                    stepInteractionName: 'my orders offers',
                                    isSeller,
                                });
                                dispatch({ type: ACTIONS.SET_STEP, step: STEPS.ORDERS_LIST });
                            }}
                            icon={<Receipt variation="24" />}
                        >
                            <FormattedMessage
                                id="getHelp.GetHelpZendeskModalIntro.ordersAndOffers"
                                defaultMessage="Orders & Offers"
                            />
                        </GetHelpZendeskIntroLink>
                        <GetHelpZendeskIntroLink
                            isLast
                            onClick={() => {
                                trackGetHelpZendeskEvent({
                                    stepInteractionName: 'my listings',
                                    isSeller,
                                });
                                dispatch({ type: ACTIONS.SET_STEP, step: STEPS.ITEMS_LIST });
                            }}
                            icon={<QuestionMarkCircle variation="24" />}
                        >
                            <FormattedMessage
                                id="getHelp.GetHelpZendeskModalIntro.listings"
                                defaultMessage="Listings"
                            />
                        </GetHelpZendeskIntroLink>
                    </>
                )}
                {!isSeller && !isCheckout && (
                    <>
                        {!isLoggedOut && (
                            <GetHelpZendeskIntroLink
                                onClick={() => {
                                    trackGetHelpZendeskEvent({
                                        stepInteractionName: 'order history',
                                    });
                                    dispatch({ type: ACTIONS.SET_STEP, step: STEPS.ORDERS_LIST });
                                }}
                                icon={<Receipt variation="24" />}
                            >
                                <FormattedMessage
                                    id="getHelp.GetHelpZendeskModalIntro.orderHistory"
                                    defaultMessage="Order History"
                                />
                            </GetHelpZendeskIntroLink>
                        )}
                        <GetHelpZendeskIntroLink
                            isLast
                            onClick={() => {
                                trackGetHelpZendeskEvent({
                                    stepInteractionName: 'common questions',
                                });
                                dispatch({ type: ACTIONS.SET_STEP, step: STEPS.COMMON_QUESTIONS });
                            }}
                            icon={<QuestionMarkCircle variation="24" />}
                        >
                            <FormattedMessage
                                id="getHelp.GetHelpZendeskModalIntro.commonQuestions"
                                defaultMessage="Common Questions"
                            />
                        </GetHelpZendeskIntroLink>
                    </>
                )}
                <GetHelpZendeskBrowseByTopic viewer={viewer} />
            </div>
        </div>
    );
};

export { GetHelpZendeskModalIntro };
